import { Link } from 'react-router-dom';
import { useContactSettings } from '@/hooks/useContactSettings';
import { ContactSettingsDialog } from '@/components/admin/ContactSettingsDialog';
import { useIsAdmin } from '@/hooks/useUserRole';
import { useFooterSettings } from '@/hooks/useFooterSettings';

export function Footer() {
  const { contactSettings } = useContactSettings();
  const { isAdmin } = useIsAdmin();
  const { footerSettings, resourceLinks, usefulLinks, isLoading } = useFooterSettings();

  // Valores padrão enquanto carrega ou se não houver dados
  const description = footerSettings?.company_description || 
    'Conectando os melhores talentos em dados com as empresas mais inovadoras do Brasil.';
  const copyrightYear = footerSettings?.copyright_year || new Date().getFullYear().toString();
  const copyrightText = footerSettings?.copyright_text || 'Data Hire Club. Todos os direitos reservados.';

  // Links padrão caso não haja dados no banco
  const defaultResourceLinks = [
    { label: 'Como Conseguir Vaga em Dados', url: '/guias/primeira-vaga-dados-2025', is_external: false, is_highlighted: false },
    { label: 'Guia de Salários 2026', url: '/guias/salarios-dados-2026', is_external: false, is_highlighted: false },
    { label: 'Transição de Carreira', url: '/guias/transicao-carreira-dados', is_external: false, is_highlighted: false },
    { label: 'Acessar todos os artigos →', url: '/guias#guias-topo', is_external: false, is_highlighted: true },
  ];

  const defaultUsefulLinks = [
    { label: 'Vagas de Dados', url: '/vagas', is_external: false },
    { label: 'Vagas de Analista de Dados', url: '/vagas/analista-de-dados', is_external: false },
    { label: 'Análise de Dados', url: '/analise-de-dados', is_external: false },
    { label: 'Para Empresas', url: '/para-empresas', is_external: false },
    { label: 'Comunidade', url: '/comunidade', is_external: false },
    { label: 'Sobre Nós', url: '/sobre', is_external: false },
    { label: 'Termos de Uso', url: '/termos', is_external: false },
    { label: 'Política de Privacidade', url: '/privacidade', is_external: false },
  ];

  const displayResourceLinks = resourceLinks.length > 0 ? resourceLinks : defaultResourceLinks;
  const displayUsefulLinks = usefulLinks.length > 0 ? usefulLinks : defaultUsefulLinks;

  return (
    <footer className="bg-black text-white py-8 mt-auto">
      <div className="container px-4 md:px-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-8">
          <div className="text-center md:text-left">
            <img 
              alt="Data Hire Club - Plataforma de vagas e comunidade para profissionais de dados" 
              className="h-16 w-auto mb-4 mx-auto md:mx-0" 
              src="/lovable-uploads/logo-horizontal.png"
              width="200"
              height="64"
            />
            <p className="text-gray-300">
              {description}
            </p>
          </div>
          <div className="text-center md:text-left">
            <div className="flex items-center justify-center md:justify-start gap-2 mb-4">
              <h4 className="text-lg font-bold">Contato</h4>
              {isAdmin && <ContactSettingsDialog />}
            </div>
            {contactSettings ? (
              <p className="text-gray-300">
                Email: {contactSettings.email}<br />
                Tel: {contactSettings.phone}<br />
                {contactSettings.location}
              </p>
            ) : (
              <p className="text-gray-300">
                Email: contato@datahireclub.com.br<br />
                Tel: (11) 4002-8922<br />
                São Paulo, SP
              </p>
            )}
          </div>
          <div className="text-center md:text-left">
            <h4 className="text-lg font-bold mb-4">Recursos</h4>
            <ul className="space-y-2">
              {displayResourceLinks.map((link, index) => (
                <li key={index}>
                  {link.is_external ? (
                    <a 
                      href={link.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className={link.is_highlighted 
                        ? "text-primary hover:text-primary/80 font-semibold transition-colors inline-flex items-center gap-1"
                        : "text-gray-300 hover:text-white transition-colors"
                      }
                    >
                      {link.label}
                    </a>
                  ) : (
                    <Link 
                      to={link.url} 
                      className={link.is_highlighted 
                        ? "text-primary hover:text-primary/80 font-semibold transition-colors inline-flex items-center gap-1"
                        : "text-gray-300 hover:text-white transition-colors"
                      }
                    >
                      {link.label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>
          <div className="text-center md:text-left">
            <h4 className="text-lg font-bold mb-4">Links Úteis</h4>
            <ul className="space-y-2">
              {displayUsefulLinks.map((link, index) => (
                <li key={index}>
                  {link.is_external ? (
                    <a 
                      href={link.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-gray-300 hover:text-white transition-colors"
                    >
                      {link.label}
                    </a>
                  ) : (
                    <Link 
                      to={link.url} 
                      className="text-gray-300 hover:text-white transition-colors"
                    >
                      {link.label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>
        <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-300">
          <p>&copy; {copyrightYear} {copyrightText}</p>
        </div>
      </div>
    </footer>
  );
}